import Heading from "Components/Heading";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { AiOutlineCheckCircle } from "react-icons/ai";
import { MdClose } from "react-icons/md";
import TicketAPIs from "../../APIs/ticket";
import SampleDataTable from "Components/DataTables";
import SearchForm from "Components/SearchForm";
import { toast } from "react-toastify";
import {
  allEligibleStationsSatisfied,
  baseSelectionKey,
  eligibleOfficeClearCodes,
  splitPartialUtilitySelections,
  stationIdsForStandardOfficeClear,
  toggleKeyInList,
  unselectedStationCodeIdsForLocate,
  utilitiesForStationCode,
  utilitySelectionKey,
} from "./officeClearSelection";

const OfficeClear = () => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchedData, setSearchedData] = useState([]);
  const [ticketStationCodeSelections, setTicketStationCodeSelections] =
    useState({});
  const [user, setUser] = useState();
  const [refresh, setRefresh] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const [isLoading, setIsLoading] = useState(false);

  const [officeClearLoader, setOfficeClearLoader] = useState(false);

  const toggleStationSelection = useCallback((ticketId, selectionKey) => {
    setTicketStationCodeSelections((prev) => {
      const next = { ...prev };
      const list = next[ticketId] || [];
      next[ticketId] = toggleKeyInList(list, selectionKey);
      return next;
    });
  }, []);

  const columns = useMemo(
    () => [
      {
        accessor: "ticketNo",
        Header: "Ticket No",
        Cell: ({ row }) => (
          <a
            onClick={(e) => {
              e.preventDefault();
              const url = `/dashboard/ticket-detail/${row.original.ticketNo}`;
              const windowName = `popUpWindow_${row.original.ticketNo}`;
              const windowSize = "width=800,height=600";
              window.open(url, windowName, windowSize);
            }}
            href={`/dashboard/ticket-detail/${row.original.ticketNo}`}
            rel="noopener noreferrer"
            className={`badge ${row.original?.cancelDate && "custom-badge"} ${row.original.ticketType == "EMERGENCY" ||
              row.original.priority == "EMERGENCY"
              ? "emergency"
              : row.original.ticketType == "MULTIPLE" ||
                row.original.priority == "MULTIPLE"
                ? "multiple"
                : row.original.ticketType == "PRIORITY" ||
                  row.original.priority == "PRIORITY"
                  ? "priority"
                  : row.original.ticketType == "PROJECT WORK" ||
                    row.original.priority == "PROJECT WORK"
                    ? "projectWork"
                    : row.original.ticketType == "STANDARD" ||
                      row.original.priority == "STANDARD"
                      ? "standard"
                      : "default"
              }`}
          >
            {row.original.ticketNo}
            {row.original?.cancelDate && <span className="cancel-btn"></span>}
          </a>
        ),
      },
      {
        accessor: "location",
        Header: "Location",
        Cell: ({ row }) => {
          return (
            <div style={{ whiteSpace: "pre-line" }}>
              {row.original.digAddress} {"\n"}
              {row.original.digCity}
              {row.original.company_name} {"\n"}
              {row.original.contractor_name} {"\n"}
              {row.original.region} {"\n"}
            </div>
          );
        },
      },
      {
        accessor: "entered",
        Header: "Entered",
      },
      {
        accessor: "dueDate",
        Header: "Due",
      },
      {
        accessor: "code",
        Header: "Station Code",
        Cell: ({ row }) => {
          const ticketId = row.original.id;
          const selections = ticketStationCodeSelections[ticketId] || [];
          const codes = row.original.code;
          if (!Array.isArray(codes)) return null;

          return codes.flatMap((code) => {
            const utils = utilitiesForStationCode(code);
            if (utils) {
              return utils.map((utility) => {
                const key = utilitySelectionKey(code.id, utility);
                const inputId = `station-code-${code.id}-${utility}`;
                return (
                  <div key={inputId} className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id={inputId}
                      checked={selections.includes(key)}
                      onChange={() => toggleStationSelection(ticketId, key)}
                      disabled={!code.isOfficeClear}
                    />
                    <label
                      className="form-check-label"
                      htmlFor={inputId}
                    >{`${code.name} - ${utility}`}</label>
                  </div>
                );
              });
            }
            const baseKey = baseSelectionKey(code.id);
            const inputId = `station-code-${code.id}`;
            return [
              <div key={inputId} className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={inputId}
                  checked={selections.includes(baseKey)}
                  onChange={() => toggleStationSelection(ticketId, baseKey)}
                  disabled={!code.isOfficeClear}
                />
                <label className="form-check-label" htmlFor={inputId}>
                  {code.name}
                </label>
              </div>,
            ];
          });
        },
      },
      { accessor: "segmet", Header: "Segment" },
    ],
    [ticketStationCodeSelections, toggleStationSelection]
  );

  const handleSelectionChange = (selectedIds) => {
    setSelectedIds(selectedIds);
  };

  const handleOfficeClear = async () => {
    if (selectedIds.length === 0) {
      toast.warning("Select at least one ticket.");
      return;
    }

    const allTicketsValid = selectedIds.every(
      (ticketId) =>
        ticketStationCodeSelections[ticketId] &&
        ticketStationCodeSelections[ticketId].length > 0
    );

    if (!allTicketsValid) {
      toast.warning(
        "Please select at least one station code for each selected ticket."
      );
      return;
    }

    setOfficeClearLoader(true);
    let officeClearSuccess = false;

    for (const ticketId of selectedIds) {
      try {
        const ticketData = searchedData.find((data) => data.id === ticketId);
        const eligible = eligibleOfficeClearCodes(ticketData);
        const selections = ticketStationCodeSelections[ticketId] || [];

        const unselectedIds = unselectedStationCodeIdsForLocate(
          eligible,
          selections
        );
        if (unselectedIds.length > 0) {
          try {
            await TicketAPIs.sendToLocate({
              ticketId: [ticketId],
              stationCodes: unselectedIds,
            });
          } catch (error) {
            console.error("Error sending to locate:", error);
          }
        }

        const partials = splitPartialUtilitySelections(eligible, selections);
        for (const { code, utility } of partials) {
          try {
            await TicketAPIs.utilityOfficeClear({
              ticketId,
              stationCode: code.name.trim(),
              utilityType: utility,
              status: "cleared",
            });
            await TicketAPIs.sendToLocate({ ticketId: [ticketId] });
            officeClearSuccess = true;
          } catch (error) {
            console.error("Utility office clear / send to locate:", error);
          }
        }

        const standardIds = stationIdsForStandardOfficeClear(
          eligible,
          selections
        );
        const allSatisfied = allEligibleStationsSatisfied(eligible, selections);

        for (const stationCodeId of standardIds) {
          try {
            const complete = await TicketAPIs.completeTicket({
              ticketId: [ticketId],
              userId: user?.id,
              isOfficeClear: true,
              stationCodes: [stationCodeId],
              skipComplete: !allSatisfied,
            });

            if (complete.data.success) {
              await TicketAPIs.officeClearStation({
                ticketId,
                stationCodeId,
                isOfficeCleared: true,
                userId: user?.id,
              });
              officeClearSuccess = true;
            }
          } catch (error) {
            setSelectedIds([]);
          }
        }
      } catch (error) {
        console.error("Error processing ticket:", ticketId, error);
      }
    }

    if (officeClearSuccess) {
      const selectedTicketsNumbers = searchedData
        .filter((item) => selectedIds.indexOf(item.id) > -1)
        .map((item) => item.ticketNo);
      setSuccessMsg(
        `Following Tickets ${selectedTicketsNumbers.join(
          ","
        )} has been cleared.`
      );
      setSelectedIds([]);
      setTicketStationCodeSelections({});
      setTimeout(() => {
        setRefresh(!refresh);
        window.location.reload();
      }, 100);
    }
    setOfficeClearLoader(false);
  };
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    setUser(user);
  }, []);

  return (
    <>
      <section>
        <Heading text={"Office Clear"} />

        <Card>
          <Row>
            <SearchForm
              setSearchedData={setSearchedData}
              setIsLoading={setIsLoading}
              filters={{ isOfficeClear: true, completedDate: false }}
              loadDataOnMount={true}
              refresh={refresh}
              from="officeClear"
              hiddenElements={["cancelDate"]}
            />
          </Row>
          <Row>
            <Col md={12} className="mb-3">
              <Button
                className="me-3"
                disabled={officeClearLoader}
                onClick={handleOfficeClear}
              >
                <AiOutlineCheckCircle />
                {officeClearLoader ? "Loading..." : "Office Clear"}
              </Button>
              <Button
                onClick={async () => {
                  if (selectedIds?.length > 0) {
                    await TicketAPIs.sendToLocate({
                      ticketId: selectedIds,
                    });
                    setRefresh(!refresh);
                    const selectedTicketsNumbers = searchedData
                      .filter((item) => selectedIds.indexOf(item.id) > -1)
                      .map((item) => item.ticketNo);
                    setSuccessMsg(
                      `Following Tickets ${selectedTicketsNumbers.join(
                        ","
                      )} has been send to locate.`
                    );
                  } else {
                    toast.warning("Select atleast one ticket");
                  }
                }}
              >
                <AiOutlineCheckCircle />
                Send to Locate
              </Button>
            </Col>
          </Row>

          {successMsg && (
            <div className="successMsg">
              <strong style={{ margin: 0, color: "rgb(0,120,0)" }}>
                {successMsg}
              </strong>
              <MdClose
                size={20}
                color={"gray"}
                onClick={() => setSuccessMsg("")}
                style={{ position: "absolute", top: 10, right: 10 }}
              />
            </div>
          )}
          <SampleDataTable
            columns={columns}
            data={searchedData}
            onSelectionChange={handleSelectionChange}
          />
        </Card>
      </section>
    </>
  );
};

export default OfficeClear;
